import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Loader2, Plus, Trash2, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS } from '@staff-app/graphql-client';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import {
  addWifiValue,
  errorText,
  parseWifiReply,
  removeWifiValue,
  validateWifi,
  WIFI_SECURITY_TYPES,
  type WifiAccessPoint,
} from '../lib/device-commands';
import {
  TestingConfirm,
  TestingDialogFrame,
  type TestingCommandDialogProps,
} from './TestingDialogFrame';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

/**
 * More › Add/Remove WiFi — port of scio3 `WiFiManagementDialog`. Saved networks from mdmDetails
 * GET_WIFI; add = mdmCommand ADD_WIFI_AP with `[{wifi_ssid, wifi_password, wifi_security_type,
 * wifi_phase2_auth:'NONE', hidden}]`; remove (confirmed first) = REMOVE_WIFI_AP with `[ssid]`.
 * The list updates optimistically — some players never answer GET_WIFI.
 */
export function TestingWifiDialog({ open, detail, commands, onClose, onDone }: TestingCommandDialogProps) {
  const scope = useDrawerScopeVars();
  const { data, loading, error } = useQuery<{ staffProxyTestingDeviceMdmDetails: string | null }>(
    STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS,
    {
      variables: { deviceId: detail._id, command: 'GET_WIFI', fresh: true, ...scope },
      skip: !open,
      fetchPolicy: 'network-only',
    },
  );
  const reported = parseWifiReply(data?.staffProxyTestingDeviceMdmDetails);
  // Local edits over the reported list (optimistic add / remove).
  const [added, setAdded] = useState<WifiAccessPoint[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const networks = [
    ...reported.filter((n) => !added.some((a) => a.wifi_ssid === n.wifi_ssid)),
    ...added,
  ].filter((n) => !removed.includes(n.wifi_ssid));

  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [securityType, setSecurityType] = useState<string>('WPA2');
  const [hidden, setHidden] = useState(false);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);

  const add = async () => {
    const problem = validateWifi({ ssid, password, securityType });
    setFormError(problem);
    if (problem) return;
    setBusy(true);
    try {
      await commands.mdmCommand('ADD_WIFI_AP', addWifiValue({ ssid, password, securityType, hidden }));
      toast.success('Wi-Fi network sent to the device');
      setAdded((prev) => [
        ...prev.filter((n) => n.wifi_ssid !== ssid),
        { wifi_ssid: ssid, wifi_security_type: securityType, hidden },
      ]);
      setRemoved((prev) => prev.filter((s) => s !== ssid));
      setSsid('');
      setPassword('');
      setHidden(false);
      onDone();
    } catch (err) {
      toast.error(errorText(err, 'Failed to add Wi-Fi network'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: string) => {
    setRemoveTarget(null);
    setBusy(true);
    try {
      await commands.mdmCommand('REMOVE_WIFI_AP', removeWifiValue(target));
      toast.success(`Removed ${target}`);
      setRemoved((prev) => [...prev, target]);
      onDone();
    } catch (err) {
      toast.error(errorText(err, 'Failed to remove Wi-Fi network'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Add/Remove WiFi'}
      icon={Wifi}
      testId="testing-wifi-dialog"
      size="lg"
      closeLabel={'Close'}
    >
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">{'Saved networks'}</div>
        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {'Reading saved networks…'}
          </div>
        ) : networks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {error
              ? `Couldn't read saved networks: ${error.message}`
              : 'The device reported no saved networks.'}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {networks.map((n) => (
              <li
                key={n.wifi_ssid}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                data-testid="testing-wifi-row"
              >
                <span className="min-w-0 truncate">
                  <span className="font-medium">{n.wifi_ssid}</span>
                  {n.wifi_security_type ? (
                    <span className="ml-2 text-xs text-muted-foreground">{n.wifi_security_type}</span>
                  ) : null}
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 text-destructive"
                  aria-label={`Remove ${n.wifi_ssid}`}
                  disabled={busy}
                  onClick={() => setRemoveTarget(n.wifi_ssid)}
                  data-testid="testing-wifi-remove"
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-3 rounded-md border p-3">
        <div className="text-xs font-semibold text-muted-foreground">{'Add a network'}</div>
        <div className="space-y-2">
          <Label htmlFor="testing-wifi-ssid">{'Wi-Fi SSID'}</Label>
          <Input id="testing-wifi-ssid" value={ssid} onChange={(e) => setSsid(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="testing-wifi-security">{'Security type'}</Label>
          <Select value={securityType} onValueChange={setSecurityType}>
            <SelectTrigger id="testing-wifi-security" className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WIFI_SECURITY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="testing-wifi-password">{'Wi-Fi Password'}</Label>
          <Input
            id="testing-wifi-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="testing-wifi-hidden"
            checked={hidden}
            onCheckedChange={(c) => setHidden(c === true)}
          />
          <Label htmlFor="testing-wifi-hidden" className="cursor-pointer text-sm font-normal">
            {'Hidden network'}
          </Label>
        </div>
        {formError ? (
          <p className="text-xs text-destructive" data-testid="testing-wifi-error">
            {formError}
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button size="sm" onClick={() => void add()} disabled={busy} data-testid="testing-wifi-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" />}
            {'Add'}
          </Button>
        </div>
      </div>

      <TestingConfirm
        open={removeTarget !== null}
        title={'Remove Wi-Fi network'}
        description={`Remove "${removeTarget ?? ''}" from this device? If it is the network the device is on, it will go offline.`}
        confirmLabel={'Remove'}
        onConfirm={() => removeTarget && void remove(removeTarget)}
        onCancel={() => setRemoveTarget(null)}
      />
    </TestingDialogFrame>
  );
}
